import { create } from "zustand";
import { useJobPostingStore } from "./jobPostingStore";
import JobPostingService from "../services/JobPostingService";
import { getDraftPayload } from "../components/JobPostingv2/utils";
import { useTemporaryJobPostingStore } from "./temporaryJobPostingStore";

export type StepState = "COMPLETE" | "INCOMPLETE" | "ERROR";

export interface JobPostingStep {
  id: string;
  state: StepState;
  name: string;
}

interface JobPostingStepperState {
  steps: JobPostingStep[];
  activeTabId: string;
  setActiveTab: (id: string) => void;
  setActiveTabDirectly: (id: string) => void; // For programmatic navigation, bypasses canNavigateToStep check
  goToNextStep: (options?: { skipSilentJobPosting?: boolean }) => void;
  goToPreviousStep: () => void;
  canGoBack: () => boolean;
  canGoNext: () => boolean;
  updateStepState: (id: string, state: StepState) => void;
  completeStep: (
    id: string,
    options?: { skipSilentJobPosting?: boolean }
  ) => void;
  canNavigateToStep: (id: string) => boolean;
  resetToBasicInfo: () => void;
  resetStore: () => void;
  updateAllStepsState: (steps: JobPostingStep[]) => void;
}

export const useJobPostingStepperStore = create<JobPostingStepperState>(
  (set, get) => ({
    steps: [
      {
        id: "basic-info",
        state: "INCOMPLETE",
        name: "Basic Info"
      },
      {
        id: "job-details",
        state: "INCOMPLETE",
        name: "Job Details"
      },
      {
        id: "candidate-details",
        state: "INCOMPLETE",
        name: "Candidate Info"
      },
      {
        id: "job-description",
        state: "INCOMPLETE",
        name: "Job Description"
      },
      {
        id: "confirmation",
        state: "INCOMPLETE",
        name: "Confirmation"
      }
    ],
    activeTabId: "basic-info",

    resetToBasicInfo: () => {
      set({
        activeTabId: "basic-info"
      });
    },

    setActiveTab: (id: string) => {
      const { canNavigateToStep } = get();

      // Only allow navigation to allowed steps
      if (!canNavigateToStep(id)) {
        return;
      }

      set({
        activeTabId: id
      });
    },

    setActiveTabDirectly: (id: string) => {
      set({
        activeTabId: id
      });
    },

    goToNextStep: (options?: { skipSilentJobPosting?: boolean }) => {
      const { steps, activeTabId, completeStep } = get();
      // Get the validation function from jobPostingStore
      const jobPostingStore = useJobPostingStore.getState();
      // Validate current step before proceeding
      const isValid = jobPostingStore.validateAndProceed(activeTabId);
      if (!isValid) {
        // Validation failed, don't proceed to next step
        return false;
      }
      useTemporaryJobPostingStore
        .getState()
        .hydrateFromData(jobPostingStore.localState);
      // Validation passed - mark current step as complete
      completeStep(activeTabId, options);

      const currentIndex = steps.findIndex(step => step.id === activeTabId);
      const nextStep = steps[currentIndex + 1];

      if (nextStep) {
        get().setActiveTabDirectly(nextStep.id);
      }
      return true;
    },

    goToPreviousStep: () => {
      const { steps, activeTabId } = get();
      const currentIndex = steps.findIndex(step => step.id === activeTabId);
      const previousStep = steps[currentIndex - 1];
      if (previousStep) {
        get().setActiveTabDirectly(previousStep.id);
      }
    },

    canGoBack: () => {
      const { steps, activeTabId } = get();
      const currentIndex = steps.findIndex(step => step.id === activeTabId);
      return currentIndex > 0;
    },

    canGoNext: () => {
      const { steps, activeTabId } = get();
      const currentIndex = steps.findIndex(step => step.id === activeTabId);
      return currentIndex < steps.length - 1;
    },

    updateStepState: (id: string, newState: StepState) => {
      set(state => ({
        steps: state.steps.map(step =>
          step.id === id ? { ...step, state: newState } : step
        )
      }));
    },
    updateAllStepsState: (steps: JobPostingStep[]) => {
      set(state => ({
        steps: steps.map(step => ({ ...step, state: step.state }))
      }));
    },

    completeStep: (
      id: string,
      options?: { skipSilentJobPosting?: boolean }
    ) => {
      set(state => ({
        steps: state.steps.map(step =>
          step.id === id ? { ...step, state: "COMPLETE" } : step
        )
      }));

      // Skip silent job posting if requested (e.g., for job edits on non-confirmation steps)
      if (options?.skipSilentJobPosting) {
        console.log("Skipping silent job posting for step:", id);
        return;
      }

      // API call on step completion - service will auto-detect confirmation step
      try {
        const jobPayload = getDraftPayload();
        if (jobPayload && Object.keys(jobPayload).length > 0) {
          JobPostingService.postJobSilent(jobPayload)
            .then(response => {
              // Handle successful response and update job posting store
              // postJobSilent returns response.data, so the actual data might be in response.data or response
              const responseData = response?.data || response;
              if (responseData) {
                const { job_id, draft_id } = responseData;
                const jobPostingStore = useJobPostingStore.getState();

                // Update job posting store with response data
                if (job_id) {
                  jobPostingStore.setJobId(job_id.toString());
                  console.log("Silent job posting: Updated jobId to", job_id);
                }

                if (draft_id) {
                  jobPostingStore.setDraftId(draft_id.toString());
                  console.log(
                    "Silent job posting: Updated draftId to",
                    draft_id
                  );
                }
              }
            })
            .catch(error => {
              console.warn("Job posting failed:", error);
              // Don't throw or show errors - this is a silent operation
            });
        }
      } catch (error) {
        console.warn("Failed to get job payload for posting:", error);
      }
    },

    canNavigateToStep: (targetId: string) => {
      const { steps, activeTabId } = get();
      const targetIndex = steps.findIndex(step => step.id === targetId);
      const currentIndex = steps.findIndex(step => step.id === activeTabId);

      if (targetIndex === -1) return false;

      // Can always navigate to current step
      if (targetIndex === currentIndex) return true;

      // Can navigate to any previous step (steps before current)
      if (targetIndex < currentIndex) {
        return true;
      }

      // Cannot navigate to next steps by clicking - must use Save & Continue button
      return false;
    },

    resetStore: () => {
      set({
        steps: [
          {
            id: "basic-info",
            state: "INCOMPLETE",
            name: "Basic Info"
          },
          {
            id: "job-details",
            state: "INCOMPLETE",
            name: "Job Details"
          },
          {
            id: "candidate-details",
            state: "INCOMPLETE",
            name: "Candidate Info"
          },
          {
            id: "job-description",
            state: "INCOMPLETE",
            name: "Job Description"
          },
          {
            id: "confirmation",
            state: "INCOMPLETE",
            name: "Confirmation"
          }
        ],
        activeTabId: "basic-info"
      });
    }
  })
);
